import { useState, useEffect } from "react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { NewsletterSettingsCard } from "../components/newsletter/NewsletterSettingsCard";
import { useToast } from "../components/ui/ToastModal";
import {
  Save,
  Loader2,
  Building2,
  CreditCard,
  Percent,
  FolderSearch,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { logError } from "../lib/logError";
import {
  CLAIMS_AUTO_AI_MODEL,
  claimsAiAutoLabel,
  claimsAiModelHint,
  claimsAiOptionLabel,
  groupClaimsAiModels,
} from "../lib/claimsAiModels";
import { useClaimsAiModels } from "../hooks/useClaimsAiModels";

interface DashpivotProject {
  id: string;
  job_location: string;
  dashpivot_project_folder_id: string | null;
  dashpivot_foreman_folder_id: string | null;
  dashpivot_foreman_app_id: string | null;
}

interface SettingsMap {
  [key: string]: string;
}

export function SettingsPage() {
  const [settings, setSettings] = useState<SettingsMap>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();
  const aiModels = useClaimsAiModels();

  // Dashpivot discovery state
  const [dpProjects, setDpProjects] = useState<DashpivotProject[]>([]);
  const [dpLoading, setDpLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
    fetchDashpivotStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashpivotStatus = async () => {
    setDpLoading(true);
    try {
      const { data, error } = await supabase
        .from("claims_projects")
        .select(
          "id, job_location, dashpivot_project_folder_id, dashpivot_foreman_folder_id, dashpivot_foreman_app_id",
        )
        .order("job_location");
      if (error) throw error;
      setDpProjects(data || []);
    } catch (error) {
      logError("Error fetching Dashpivot status:", error);
    } finally {
      setDpLoading(false);
    }
  };

  const runDiscovery = async () => {
    setDiscovering(true);
    setDiscoveryResult(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/discover-dashpivot-folders`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        },
      );
      const result = await res.json();
      if (result.success) {
        setDiscoveryResult(
          `✓ Mapped ${result.mapped}/${result.total} projects`,
        );
        addToast({
          type: "success",
          title: `Discovery complete: ${result.mapped}/${result.total} mapped`,
        });
        fetchDashpivotStatus();
      } else {
        setDiscoveryResult(`✗ Error: ${result.error}`);
        addToast({ type: "error", title: "Discovery failed" });
      }
    } catch (error) {
      logError("Discovery error:", error);
      setDiscoveryResult("✗ Network error");
      addToast({ type: "error", title: "Discovery failed" });
    } finally {
      setDiscovering(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("claims_settings")
        .select("setting_key, setting_value");

      if (error) throw error;

      const settingsMap: SettingsMap = {};
      data?.forEach((item) => {
        settingsMap[item.setting_key] = item.setting_value || "";
      });
      setSettings(settingsMap);
    } catch (error) {
      logError("Error fetching settings:", error);
      addToast({ type: "error", title: "Failed to load settings" });
    } finally {
      setLoading(false);
    }
  };

  const selectedAiModel = settings.ai_model || CLAIMS_AUTO_AI_MODEL;
  const savedModelListed = selectedAiModel === CLAIMS_AUTO_AI_MODEL ||
    aiModels.list.models.some((m) => m.id === selectedAiModel);

  const handleChange = (key: string, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const orgId = requireOrganizationId();
      const upsertData = Object.entries(settings).map(([key, value]) => ({
        organization_id: orgId,
        setting_key: key,
        setting_value: value,
        updated_at: new Date().toISOString(),
      }));

      for (const item of upsertData) {
        const { error } = await supabase
          .from("claims_settings")
          .upsert(item, { onConflict: "setting_key" });

        if (error) throw error;
      }

      addToast({ type: "success", title: "Settings saved successfully" });
    } catch (error) {
      logError("Error saving settings:", error);
      addToast({ type: "error", title: "Failed to save settings" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="app-content">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "50vh",
            gap: 16,
          }}
        >
          <div className="skeleton" style={{ width: 200, height: 24 }} />
          <div className="skeleton" style={{ width: 300, height: 14 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", maxWidth: 600, marginTop: 24 }}>
            <div className="skeleton" style={{ width: "100%", height: 200, borderRadius: "var(--border-radius-lg)" }} />
            <div className="skeleton" style={{ width: "100%", height: 160, borderRadius: "var(--border-radius-lg)" }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-content">
      {/* Header Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "var(--spacing-xl)",
          flexWrap: "wrap",
          gap: "var(--spacing-md)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.75rem", fontWeight: 700, margin: 0 }}>
            Settings
          </h1>
          <p style={{ marginTop: "4px", maxWidth: "600px" }}>
            Manage company details, bank accounts, and claim defaults.
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary"
          style={{ minWidth: "140px" }}
        >
          {saving ? (
            <Loader2 className="spinning" size={16} />
          ) : (
            <Save size={16} />
          )}
          <span>{saving ? "Saving..." : "Save Changes"}</span>
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {/* Bank Details */}
        <div className="card">
          <div className="card-header">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  padding: "10px",
                  background: "rgba(59, 130, 246, 0.1)",
                  borderRadius: "8px",
                }}
              >
                <CreditCard size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="card-title">Bank Details</h2>
                <p style={{ fontSize: "0.875rem" }}>
                  Provide the bank account for payments to be displayed on
                  invoices.
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "var(--spacing-lg)",
            }}
          >
            <div className="form-group">
              <label className="form-label">Account Name</label>
              <input
                type="text"
                value={settings.bank_account_name || ""}
                onChange={(e) =>
                  handleChange("bank_account_name", e.target.value)
                }
                className="form-input"
                placeholder="e.g. Acme Corp Pty Ltd"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Bank Name</label>
              <input
                type="text"
                value={settings.bank_name || ""}
                onChange={(e) => handleChange("bank_name", e.target.value)}
                className="form-input"
                placeholder="e.g. Commonwealth Bank"
              />
            </div>

            <div className="form-group">
              <label className="form-label">BSB</label>
              <input
                type="text"
                value={settings.bank_bsb || ""}
                onChange={(e) => handleChange("bank_bsb", e.target.value)}
                className="form-input"
                placeholder="000-000"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Account Number</label>
              <input
                type="text"
                value={settings.bank_account || ""}
                onChange={(e) => handleChange("bank_account", e.target.value)}
                className="form-input"
                placeholder="00000000"
              />
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="card">
          <div className="card-header">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  padding: "10px",
                  background: "rgba(59, 130, 246, 0.1)",
                  borderRadius: "8px",
                }}
              >
                <Building2 size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="card-title">Company Information</h2>
                <p style={{ fontSize: "0.875rem" }}>
                  Business details that will appear on all generated documents.
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "24px",
            }}
          >
            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input
                type="text"
                value={settings.company_name || ""}
                onChange={(e) => handleChange("company_name", e.target.value)}
                className="form-input"
                placeholder="e.g. Acme Corp"
              />
            </div>

            <div className="form-group">
              <label className="form-label">ABN</label>
              <input
                type="text"
                value={settings.company_abn || ""}
                onChange={(e) => handleChange("company_abn", e.target.value)}
                className="form-input"
                placeholder="00 000 000 000"
              />
            </div>

            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label className="form-label">Address</label>
              <input
                type="text"
                value={settings.company_address || ""}
                onChange={(e) =>
                  handleChange("company_address", e.target.value)
                }
                className="form-input"
                placeholder="Street Address, City, State, ZIP"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="text"
                value={settings.company_phone || ""}
                onChange={(e) => handleChange("company_phone", e.target.value)}
                className="form-input"
                placeholder="(04) 0000 0000"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                value={settings.company_email || ""}
                onChange={(e) => handleChange("company_email", e.target.value)}
                className="form-input"
                placeholder="accounts@seqformwork.com.au"
              />
            </div>
          </div>
        </div>

        {/* Claim Defaults */}
        <div className="card">
          <div className="card-header">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  padding: "10px",
                  background: "rgba(59, 130, 246, 0.1)",
                  borderRadius: "8px",
                }}
              >
                <Percent size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="card-title">Claim Defaults</h2>
                <p style={{ fontSize: "0.875rem" }}>
                  Default values used when creating new progress claims.
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "24px",
            }}
          >
            <div className="form-group">
              <label className="form-label">Default Retention (%)</label>
              <input data-seq-private
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={settings.default_retention_percentage || ""}
                onChange={(e) =>
                  handleChange("default_retention_percentage", e.target.value)
                }
                className="form-input"
                placeholder="10.0"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Default Submission Day</label>
              <input data-seq-private
                type="number"
                min="1"
                max="31"
                value={settings.default_claim_submission_day || ""}
                onChange={(e) =>
                  handleChange("default_claim_submission_day", e.target.value)
                }
                className="form-input"
                placeholder="25"
              />
              <p
                style={{
                  fontSize: "0.75rem",
                  marginTop: "4px",
                  color: "var(--text-muted)",
                }}
              >
                Day of the month (e.g., 25th)
              </p>
            </div>
          </div>
        </div>

        {/* AI Model Selection */}
        <div className="card">
          <div className="card-header">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div>
                <h2 className="card-title">AI Model</h2>
                <p style={{ fontSize: "0.875rem" }}>
                  Select the Gemini model used for all AI features (insights,
                  claim generation, payment schedule analysis).
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "24px",
            }}
          >
            <div className="form-group">
              <label className="form-label" htmlFor="ai-model-select">
                Gemini Model
              </label>
              <select
                id="ai-model-select"
                className="form-input"
                value={selectedAiModel}
                onChange={(e) => handleChange("ai_model", e.target.value)}
                disabled={aiModels.loading}
                aria-busy={aiModels.loading}
                style={{ cursor: aiModels.loading ? "wait" : "pointer" }}
              >
                <option value={CLAIMS_AUTO_AI_MODEL}>
                  {aiModels.loading
                    ? "Loading models..."
                    : claimsAiAutoLabel(aiModels.list.auto)}
                </option>
                {!aiModels.loading && !savedModelListed && (
                  <option value={selectedAiModel}>
                    {selectedAiModel} (not in the current list)
                  </option>
                )}
                {!aiModels.loading &&
                  groupClaimsAiModels(aiModels.list.models).map((group) => (
                    <optgroup key={group.family} label={group.label}>
                      {group.models.map((m) => (
                        <option key={m.id} value={m.id}>
                          {claimsAiOptionLabel(m)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
              </select>
              <p
                style={{
                  fontSize: "0.75rem",
                  marginTop: "6px",
                  color: "var(--text-muted)",
                  lineHeight: "1.4",
                }}
              >
                {claimsAiModelHint(selectedAiModel)}
              </p>
              <p
                role="status"
                style={{
                  fontSize: "0.75rem",
                  marginTop: "6px",
                  color: "var(--text-muted)",
                  lineHeight: "1.4",
                }}
              >
                {aiModels.failed && !aiModels.loading
                  ? "Could not load Google's current model list, so this is the saved fallback list. Auto still picks the newest Flash when a claim runs."
                  : ""}
              </p>
            </div>
          </div>
        </div>

        {/* Newsletter Configuration */}
        <NewsletterSettingsCard />

        {/* Dashpivot Integration */}
        <div className="card">
          <div className="card-header">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    padding: "10px",
                    background: "rgba(234, 88, 12, 0.1)",
                    borderRadius: "8px",
                  }}
                >
                  <FolderSearch size={24} color="#ea580c" />
                </div>
                <div>
                  <h2 className="card-title">Dashpivot Integration</h2>
                  <p style={{ fontSize: "0.875rem" }}>
                    Map Dashpivot project folders to sync variations from
                    Foreman folders.
                  </p>
                </div>
              </div>
              <button
                onClick={runDiscovery}
                disabled={discovering}
                className="btn"
                style={{
                  minWidth: "160px",
                  background: discovering
                    ? "var(--bg-secondary)"
                    : "rgba(234, 88, 12, 0.1)",
                  color: "#ea580c",
                  border: "1px solid rgba(234, 88, 12, 0.2)",
                }}
              >
                {discovering ? (
                  <Loader2 className="spinning" size={16} />
                ) : (
                  <RefreshCw size={16} />
                )}
                <span>{discovering ? "Discovering..." : "Run Discovery"}</span>
              </button>
            </div>
          </div>

          {discoveryResult && (
            <div
              style={{
                padding: "12px 20px",
                background: discoveryResult.startsWith("✓")
                  ? "rgba(34, 197, 94, 0.08)"
                  : "rgba(239, 68, 68, 0.08)",
                borderBottom: "1px solid var(--border-color)",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: discoveryResult.startsWith("✓") ? "#16a34a" : "#dc2626",
              }}
            >
              {discoveryResult}
            </div>
          )}

          {dpLoading ? (
            <div
              style={{
                padding: "32px",
                textAlign: "center",
                color: "var(--text-muted)",
              }}
            >
              <Loader2 className="spinning" size={20} />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.875rem",
                }}
              >
                <thead>
                  <tr
                    style={{
                      borderBottom: "1px solid var(--border-color)",
                      background: "var(--bg-secondary)",
                    }}
                  >
                    <th
                      style={{
                        padding: "10px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Project
                    </th>
                    <th
                      style={{
                        padding: "10px 16px",
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      Folder
                    </th>
                    <th
                      style={{
                        padding: "10px 16px",
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      Foreman
                    </th>
                    <th
                      style={{
                        padding: "10px 16px",
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      Variation App
                    </th>
                    <th
                      style={{
                        padding: "10px 16px",
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dpProjects.map((proj) => {
                    const hasFolder = !!proj.dashpivot_project_folder_id;
                    const hasForeman = !!proj.dashpivot_foreman_folder_id;
                    const hasApp = !!proj.dashpivot_foreman_app_id;
                    const isFullyMapped = hasFolder && hasForeman && hasApp;
                    const isPartial = hasFolder && !isFullyMapped;

                    return (
                      <tr
                        key={proj.id}
                        style={{
                          borderBottom: "1px solid var(--border-color)",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 16px",
                            fontWeight: 500,
                          }}
                        >
                          {proj.job_location}
                        </td>
                        <td
                          style={{ padding: "10px 16px", textAlign: "center" }}
                        >
                          {hasFolder ? (
                            <CheckCircle2
                              size={18}
                              color="#16a34a"
                              style={{ display: "inline" }}
                            />
                          ) : (
                            <XCircle
                              size={18}
                              color="#dc2626"
                              style={{ display: "inline" }}
                            />
                          )}
                        </td>
                        <td
                          style={{ padding: "10px 16px", textAlign: "center" }}
                        >
                          {hasForeman ? (
                            <CheckCircle2
                              size={18}
                              color="#16a34a"
                              style={{ display: "inline" }}
                            />
                          ) : (
                            <XCircle
                              size={18}
                              color="#dc2626"
                              style={{ display: "inline" }}
                            />
                          )}
                        </td>
                        <td
                          style={{ padding: "10px 16px", textAlign: "center" }}
                        >
                          {hasApp ? (
                            <CheckCircle2
                              size={18}
                              color="#16a34a"
                              style={{ display: "inline" }}
                            />
                          ) : (
                            <XCircle
                              size={18}
                              color="#dc2626"
                              style={{ display: "inline" }}
                            />
                          )}
                        </td>
                        <td
                          style={{ padding: "10px 16px", textAlign: "center" }}
                        >
                          {isFullyMapped ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "2px 10px",
                                borderRadius: "99px",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                background: "rgba(34, 197, 94, 0.1)",
                                color: "#16a34a",
                              }}
                            >
                              Synced
                            </span>
                          ) : isPartial ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "2px 10px",
                                borderRadius: "99px",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                background: "rgba(234, 179, 8, 0.1)",
                                color: "#ca8a04",
                              }}
                            >
                              <AlertTriangle size={12} />
                              Partial
                            </span>
                          ) : (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "2px 10px",
                                borderRadius: "99px",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                background: "rgba(239, 68, 68, 0.1)",
                                color: "#dc2626",
                              }}
                            >
                              Not Mapped
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div
            style={{
              padding: "12px 16px",
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              borderTop: "1px solid var(--border-color)",
            }}
          >
            {dpProjects.filter((p) => !!p.dashpivot_foreman_app_id).length}/
            {dpProjects.length} projects mapped • Contract Variance shared
            folder is always included in sync • Run discovery when adding new
            projects
          </div>
        </div>
      </div>
    </div>
  );
}
